'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { Plus, Search, Link2, CheckCircle, Clock, AlertCircle, Loader2, X, Save, Copy, CheckCheck, Share2, Trash2 } from 'lucide-react';

interface ReferralLink {
  id: string;
  patientId: string;
  examId: string;
  examType: string;
  recipientName: string;
  recipientEmail: string;
  token: string;
  expiresAt: string;
  viewedAt: string | null;
  createdAt: string;
}

export default function ReferralsPage() {
  const [referrals, setReferrals] = useState<ReferralLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    patientId: '',
    examId: '',
    examType: '',
    recipientName: '',
    recipientEmail: '',
    expiresAt: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch('/api/patients/referrals');
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const json = await res.json();
      setReferrals(json.data ?? []);
    } catch (err: any) {
      setLoadError(err.message ?? 'Errore nel caricamento dei referral');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = referrals.filter((r) =>
    !search ||
    r.recipientName.toLowerCase().includes(search.toLowerCase()) ||
    r.recipientEmail.toLowerCase().includes(search.toLowerCase()) ||
    r.examType.toLowerCase().includes(search.toLowerCase())
  );

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.patientId.trim()) errs.patientId = 'ID paziente obbligatorio';
    if (!form.examId.trim()) errs.examId = 'ID esame obbligatorio';
    if (!form.recipientName.trim()) errs.recipientName = 'Nome destinatario obbligatorio';
    if (!form.recipientEmail.trim()) {
      errs.recipientEmail = 'Email destinatario obbligatoria';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.recipientEmail.trim())) {
        errs.recipientEmail = 'Formato email non valido';
      }
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch('/api/patients/referrals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          examType: form.examType || 'Referto',
          expiresAt: form.expiresAt || undefined,
        }),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error ?? `Errore ${res.status}`);
      }
      setShowModal(false);
      setForm({ patientId: '', examId: '', examType: '', recipientName: '', recipientEmail: '', expiresAt: '' });
      load();
    } catch (err: any) {
      setSaveError(err.message ?? 'Errore nella creazione del link');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminare questo link referral? L\'azione non è reversibile.')) return;
    setDeletingId(id);
    try {
      const res = await fetch(`/api/patients/referrals/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error ?? `Errore ${res.status}`);
      }
      setReferrals((prev) => prev.filter((r) => r.id !== id));
    } catch (err: any) {
      alert(err.message ?? 'Errore nell\'eliminazione del link');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyLink = (token: string, id: string) => {
    const url = `${window.location.origin}/patient-portal/referral?token=${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  const stats = {
    total: referrals.length,
    viewed: referrals.filter((r) => r.viewedAt).length,
    expired: referrals.filter((r) => isExpired(r.expiresAt)).length,
    active: referrals.filter((r) => !r.viewedAt && !isExpired(r.expiresAt)).length,
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-white border-b border-border px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-lg font-semibold text-foreground">Referral & Condivisione Referti</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Gestisci i link di condivisione referti per i pazienti</p>
            </div>
            <button
              onClick={() => { setShowModal(true); setSaveError(null); }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
            >
              <Plus size={14} /> Nuovo Link
            </button>
          </div>
        </div>

        <div className="px-6 lg:px-8 py-6 space-y-5">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Totali', value: stats.total, icon: Share2, color: 'text-primary' },
              { label: 'Attivi', value: stats.active, icon: Clock, color: 'text-amber-600' },
              { label: 'Visualizzati', value: stats.viewed, icon: CheckCircle, color: 'text-green-600' },
              { label: 'Scaduti', value: stats.expired, icon: AlertCircle, color: 'text-red-600' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-border p-4 flex items-center gap-3">
                <s.icon size={18} className={s.color} />
                <div>
                  <p className="text-xl font-bold text-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="relative max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per destinatario o esame..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          {/* Load error */}
          {loadError && (
            <div className="flex items-center gap-2.5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={15} className="shrink-0" />
              <span>{loadError}</span>
              <button onClick={load} className="ml-auto text-xs underline hover:no-underline">Riprova</button>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 && !loadError ? (
            <div className="text-center py-20">
              <Link2 size={32} className="mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">Nessun link referral trovato</p>
              <button onClick={() => { setShowModal(true); setSaveError(null); }} className="mt-3 text-xs text-primary hover:underline">Crea il primo link</button>
            </div>
          ) : filtered.length > 0 ? (
            <div className="bg-white rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Destinatario</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Esame</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Creato</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Scadenza</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Stato</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((r) => {
                      const expired = isExpired(r.expiresAt);
                      const viewed = !!r.viewedAt;
                      return (
                        <tr key={r.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground">{r.recipientName}</p>
                            <p className="text-xs text-muted-foreground">{r.recipientEmail}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{r.examType}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.createdAt).toLocaleDateString('it-IT')}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(r.expiresAt).toLocaleDateString('it-IT')}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                              viewed ? 'bg-green-50 text-green-700 border-green-200' : expired ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                              {viewed ? <><CheckCircle size={9} /> Visualizzato</> :
                               expired ? <><AlertCircle size={9} /> Scaduto</> :
                               <><Clock size={9} /> Attivo</>}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleCopyLink(r.token, r.id)}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors"
                              >
                                {copiedId === r.id ? <CheckCheck size={12} className="text-green-600" /> : <Copy size={12} />}
                                {copiedId === r.id ? 'Copiato!' : 'Copia link'}
                              </button>
                              <button
                                onClick={() => handleDelete(r.id)}
                                disabled={deletingId === r.id}
                                className="p-1.5 rounded-lg border border-border text-xs text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                title="Elimina link"
                              >
                                {deletingId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">Nuovo Link Referral</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {saveError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                  <AlertCircle size={13} className="shrink-0" />
                  {saveError}
                </div>
              )}
              {[
                { field: 'patientId', label: 'ID Paziente *', placeholder: 'pat-123' },
                { field: 'examId', label: 'ID Esame *', placeholder: 'exam-456' },
                { field: 'examType', label: 'Tipo esame', placeholder: 'Emocromo' },
                { field: 'recipientName', label: 'Nome destinatario *', placeholder: 'Mario Rossi' },
                { field: 'recipientEmail', label: 'Email destinatario *', placeholder: 'mario@email.it' },
              ].map((f) => (
                <div key={f.field}>
                  <label className="block text-xs font-medium text-foreground mb-1">{f.label}</label>
                  <input
                    type={f.field === 'recipientEmail' ? 'email' : 'text'}
                    value={form[f.field as keyof typeof form]}
                    onChange={(e) => { setForm((p) => ({ ...p, [f.field]: e.target.value })); setFormErrors((p) => ({ ...p, [f.field]: '' })); }}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${formErrors[f.field] ? 'border-red-400' : 'border-border'}`}
                    placeholder={f.placeholder}
                  />
                  {formErrors[f.field] && <p className="text-xs text-red-500 mt-1">{formErrors[f.field]}</p>}
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Scadenza (opzionale)</label>
                <input
                  type="date"
                  value={form.expiresAt}
                  onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Default: 30 giorni dalla creazione</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors">
                Annulla
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-all"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Crea Link
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
